import type { DeviceColumn, DeviceComparator, ColumnFilter, FieldValues } from '../components/aditools-device-table';
import type {
  DeviceTableStore,
  DatabookDeviceData,
  DeviceTableDeviceData,
  DatabookColumnData,
  ParameterData,
} from '@ebiz/aditools-databook';
import Comparator from './comparator.ts';
import EngineeringFilter from './engineering-filter.ts';
import PartialTextFilter from './partial-text-filter.ts';
import PriceFilter from './price-filter.ts';
import MultivalueCheckboxFilter from './multivalue-checkbox-filter.ts';

const TRANSLATIONS = {
  en: {
    partName: 'Part Name',
    showAll: 'Show All',
    blanks: '(Blanks)',
  },
  cn: {
    // partName: '产品型号',
    partName: 'Part Name',
    showAll: '显示全部',
    blanks: '(Blanks)',
  },
  jp: {
    //partName: '製品番号',
    partName: 'Part Name',
    showAll: 'すべて表示',
    blanks: '(Blanks)',
  },
  ru: {
    partName: 'Part Name',
    showAll: 'Show All',
    blanks: '(Blanks)',
  },
  /*
  ru: {
    //partName: 'Наименование компонента',
    partName: 'Part Name',
    showAll: 'Показать все',
  },
  */
};

const setBlankValue = function (deviceData: DeviceTableDeviceData[]): DeviceTableDeviceData[] {
  const deviceDataCopy = deviceData.map((data) => {
    const dataCopy = Object.assign({}, data);
    for (const [key, value] of Object.entries(dataCopy)) {
      if (!key.startsWith('value_')) {
        if (value === '') {
          dataCopy[key] = '-';
        }
      }
    }
    return dataCopy;
  });
  return deviceDataCopy;
};

const NO_FILTER_COLUMNS = ['availability'];

type ParameterId = string; // pattern [\d]{1-4}|s[\d]{1-2}

export function convertDeviceDataForSelectionTable(
  deviceData: DatabookDeviceData[],
  columnData: DeviceColumn<DeviceTableDeviceData>[],
  blanksAsDashes?: boolean,
): DeviceTableDeviceData[] {
  const rows: DeviceTableDeviceData[] = [];
  for (const part of deviceData) {
    const row = {
      partNumber: part.partNumber?.displayValue,
      generic: part.partNumber?.generic,
      id: part.partNumber?.displayValue,
    } as DeviceTableDeviceData;
    for (const parm in part) {
      const parmdata = columnData.find((column) => column.id === parm);
      if (parm === 'id' || parm === 'partNumber' || !parmdata) continue;
      const displayKey = 'display_' + parm;
      const valueKey = 'value_' + parm;
      const partparm = part[parm] as ParameterData;
      row[displayKey] = partparm.displayValue;
      switch (parmdata.type) {
        case 'Floating Point':
          if (partparm.value[0] === '') {
            row[valueKey] = null;
          } else {
            row[valueKey] = parseFloat(partparm.value[0] ?? '0');
          }
          break;
        case 'Integer':
          if (partparm.value[0] === '') {
            row[valueKey] = null;
          } else {
            row[valueKey] = parseInt(partparm.value[0] ?? '0');
          }
          break;

        case 'Pulldown':
          // Nothing in a parameter mapping tells us if a pulldown type on a part is meant to have one value or multiple.
          // The code has to either always return an array or return a single value if that is all that is there.
          // Most pulldowns are single-value and the caller can special-case the few times a multi-value select has a single value.
          // Essentially, this code has optimized for the commonest case, rather than generalizing. Right? Wrong? We'll see.
          if (partparm.value.length === 1) {
            row[valueKey] = partparm.value[0];
          } else {
            row[valueKey] = partparm.value;
          }
          break;
        case 'Date':
          // Date string value is delivered as a Date value. Only s18 uses this so far.
          row[valueKey] = Date.parse(partparm.value[0] ?? '');
          break;
        case 'String':
          row[valueKey] = partparm.value[0];
          break;
        default:
          console.log(`Parameter ${parm} has unknown parameter type ${parmdata.type}`);
          row[valueKey] = partparm.value;
          break;
      }
    }
    rows.push(row);
  }
  return blanksAsDashes ? setBlankValue(rows) : rows;
}

export function convertColumnDataForSelectionTable(
  columnData: DatabookColumnData,
  locale = 'en',
): DeviceColumn<DeviceTableDeviceData> {
  const field: ParameterId = columnData.field as ParameterId;
  let columnComparator: DeviceComparator<string>;
  switch (columnData.type) {
    case 'Integer':
      columnComparator = Comparator.integerComparator;
      break;
    case 'Floating Point':
      columnComparator = Comparator.engineeringComparator;
      break;
    case 'Pulldown':
      columnComparator = Comparator.textComparator;
      break;
    case 'Date':
      columnComparator = Comparator.textComparator;
      break;
    case 'String':
      columnComparator = Comparator.textComparator;
      break;
    default:
      columnComparator = Comparator.textComparator;
      break;
  }
  let columnFilter: ColumnFilter<DeviceTableDeviceData> | undefined;
  let columnValues: FieldValues[] = [];
  if (!NO_FILTER_COLUMNS.includes(field)) {
    switch (columnData.filterType) {
      case 'Sliders': {
        if (columnData.uom === '$ US') {
          columnFilter = new PriceFilter('GT');
        } else {
          columnFilter = new EngineeringFilter('GT');
        }
        break;
      }
      case 'Textbox': {
        columnFilter = new PartialTextFilter('');
        break;
      }
      case 'Checkboxes': {
        const fieldValues: FieldValues[] = [];
        if (columnData.values) {
          fieldValues.push({
            value: '-',
            display: _translations(locale)['blanks'] ?? '(Blanks)',
            match: '',
          });
          columnData.values.forEach((item) => {
            fieldValues.push({
              value: item.toString(),
              display: item.toString(),
              match: item.toString(),
            });
          });
        }
        columnValues = fieldValues;
        columnFilter = new MultivalueCheckboxFilter(fieldValues);
        break;
      }
      default: {
        columnFilter = new PartialTextFilter('');
        break;
      }
    }
  }

  const column: DeviceColumn<DeviceTableDeviceData> = {
    id: columnData.field,
    name: columnData.name,
    field: columnData.field === 'partNumber' ? columnData.field : 'display_' + columnData.field,
    sortable: true,
    comparator: columnComparator,
    filter: columnFilter,
    columnValues: columnValues,
    classification: columnData.classification,
    isDefault: columnData.displayInDefaultView,
    unit: columnData.uom,
    minMaxTyp: columnData.minMaxTyp,
    toolTip: columnData.definition ?? columnData.name,
  };
  return column;
}

export function reduceColumnFilterValuesToThosePresent(
  rowData: DeviceTableDeviceData[],
  columnData: DeviceColumn<DeviceTableDeviceData>[],
): void {
  for (const column of columnData) {
    if (column.columnValues && column.columnValues.length > 0) {
      const fieldValues: FieldValues[] = [];
      for (const columnValue of column.columnValues) {
        const checkData = rowData.filter((x) => {
          const matchValue = typeof columnValue.match !== 'undefined' ? columnValue.match : columnValue.value;
          if (Array.isArray(x[`value_${column.id as string}`])) {
            const xvalues = (x[`${column.field as string}`] as string).split(',').map((s) => s.trim());
            return matchValue !== ''
              ? xvalues.includes(matchValue.trim())
              : xvalues.includes(matchValue.trim()) || xvalues.includes('-');
          } else {
            return matchValue !== ''
              ? x[`${column.field as string}`] === matchValue
              : x[`${column.field as string}`] === matchValue || x[`${column.field as string}`] === '-';
          }
        });
        if (checkData.length > 0) {
          fieldValues.push(columnValue);
        }
      }
      column.filter = new MultivalueCheckboxFilter(fieldValues);
    }
  }
}

export function getDeviceTableTitle(deviceTableStore: DeviceTableStore): string {
  return deviceTableStore.title;
}

function _translations(locale: string): Record<string, string> {
  let loc: 'en' | 'cn' | 'jp' | 'ru' = 'en';
  if (locale === 'en' || locale === 'cn' || locale === 'jp' || locale === 'ru') {
    loc = locale;
  }
  return TRANSLATIONS[loc as 'en' | 'cn' | 'jp' | 'ru'];
}
